import { useState } from 'react'
import { FileCsv, UploadSimple } from '@phosphor-icons/react'
import { useAuth } from '@/auth/context'
import { Banner, Button, DataTable, Field, Input, TextArea, type Column } from '@/ui'
import { errorMessage } from '../api/core'
import { useImportAdvances, type AdvanceEntry } from '../api/payouts'
import { searchUsers, type UserOption } from '../api/reference'
import { paiseToInrString } from '../api/mappers'
import { ActionButton } from '../components/Can'
import { ConsolePage } from '../components/Layout'
import { PersonPicker } from '../components/PersonPicker'
import { Money, Section } from '../components/bits'
import { parseAdvanceCsv, type ParsedAdvance } from '../model/advances'

interface Row extends ParsedAdvance {
  farmer: UserOption | null
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Advances import (F4.9-p): outstanding advances from the old records, signed off by finance. */
export function AdvancesPage() {
  const { user } = useAuth()
  const [text, setText] = useState('')
  const [rows, setRows] = useState<Row[]>([])
  const [resolving, setResolving] = useState(false)
  const [batchRef, setBatchRef] = useState('')
  const [finance, setFinance] = useState<UserOption | null>(null)
  const [signedOff, setSignedOff] = useState(false)
  const imp = useImportAdvances()

  const resolve = async () => {
    setResolving(true)
    const parsed = parseAdvanceCsv(text)
    const out: Row[] = []
    const cache = new Map<string, UserOption | null>()
    for (const r of parsed) {
      let farmer: UserOption | null = null
      if (!r.problem) {
        if (!cache.has(r.who)) {
          try {
            const found = await searchUsers(r.who, 10)
            cache.set(
              r.who,
              found.rows.find((u) => (UUID.test(r.who) ? u.id === r.who : u.farmerCode?.toLowerCase() === r.who.toLowerCase())) ??
                (UUID.test(r.who) ? { id: r.who, name: r.who, username: '', farmerCode: null, contactNo: null, roles: [] } : null),
            )
          } catch {
            cache.set(r.who, null)
          }
        }
        farmer = cache.get(r.who) ?? null
      }
      out.push({ ...r, farmer, problem: r.problem ?? (farmer ? null : 'No farmer with this roster number') })
    }
    setRows(out)
    setResolving(false)
  }

  const good = rows.filter((r) => !r.problem && r.farmer)
  const total = good.reduce((s, r) => s + (r.amountPaise ?? 0), 0)
  const sameAsMe = Boolean(finance && user && finance.id === user.id)
  const ready = good.length > 0 && good.length === rows.length && batchRef.trim() && finance && !sameAsMe && signedOff

  const submit = async () => {
    if (!ready || !finance) return
    const entries: AdvanceEntry[] = good.map((r) => ({
      farmerId: r.farmer!.id,
      amountInr: paiseToInrString(r.amountPaise!),
      occurredAt: r.occurredAt,
      externalRef: r.externalRef,
    }))
    try {
      await imp.mutateAsync({ batchRef: batchRef.trim(), financeSignedOff: true, financeSignOffById: finance.id, entries })
    } catch {
      /* toasted */
    }
  }

  const columns: Column<Row>[] = [
    { id: 'line', header: 'Line', mono: true, cell: (r) => r.line, width: '60px' },
    { id: 'who', header: 'Roster no.', mono: true, cell: (r) => r.who || '—' },
    { id: 'farmer', header: 'Farmer', cell: (r) => r.farmer?.name ?? <span className="text-muted">—</span> },
    { id: 'amount', header: 'Amount', align: 'right', cell: (r) => <Money paise={r.amountPaise} /> },
    { id: 'date', header: 'Given on', mono: true, cell: (r) => r.occurredAt || '—', hideBelow: 'sm' },
    { id: 'ref', header: 'Reference', mono: true, cell: (r) => r.externalRef || '—', hideBelow: 'md' },
    { id: 'problem', header: 'Check', cell: (r) => (r.problem ? <span className="text-[13px] text-danger">{r.problem}</span> : <span className="text-[13px] text-ok">OK</span>) },
  ]

  return (
    <ConsolePage
      eyebrow="Payments"
      title="Advances import"
      description="Bring outstanding advances from the old records into the ledger as ADVANCE lines. Finance signs the list off — a different person from whoever imports it. Running the same batch again skips rows already imported."
    >
      <Section title="1 · The list" eyebrow="CSV: roster no, amount ₹, date (YYYY-MM-DD), reference">
        <div className="flex flex-col gap-3">
          <input
            type="file"
            accept=".csv,text/csv"
            aria-label="Load a CSV file"
            className="text-[13px]"
            onChange={async (e) => {
              const f = e.target.files?.[0]
              if (f) setText(await f.text())
            }}
          />
          <TextArea rows={7} value={text} onChange={(e) => setText(e.target.value)} className="font-mono text-[12.5px]" placeholder={'roster_no,amount_inr,date,ref\nBELU-017,1500.00,2026-06-12,ADV-2291'} />
          <div>
            <Button variant="secondary" leadingIcon={<FileCsv />} loading={resolving} disabled={!text.trim()} onClick={() => void resolve()}>
              Check the list
            </Button>
          </div>
        </div>
      </Section>

      {rows.length > 0 && (
        <DataTable<Row>
          caption="Advances to import"
          columns={columns}
          rows={rows}
          getRowId={(r) => String(r.line)}
          pageSize={50}
          filters={
            <span className="font-mono text-[11px] font-bold uppercase tracking-label text-muted">
              {good.length} of {rows.length} rows OK · <Money paise={total} />
            </span>
          }
        />
      )}

      {rows.length > 0 && (
        <Section title="2 · Sign-off and import">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Batch reference" required hint="The same reference re-run is safe.">
              <Input value={batchRef} onChange={(e) => setBatchRef(e.target.value)} className="font-mono" placeholder="e.g. ADV-2026-OCT-01" />
            </Field>
            <Field label="Signed off by (finance)" required error={sameAsMe ? 'Finance sign-off is by someone other than you' : null}>
              <PersonPicker value={finance} onChange={setFinance} label="Finance" placeholder="Search the finance person" />
            </Field>
          </div>
          <label className="mt-4 flex items-center gap-2 text-[14px] font-semibold">
            <input type="checkbox" className="h-4 w-4 accent-amber" checked={signedOff} onChange={(e) => setSignedOff(e.target.checked)} />
            Finance has signed off this list
          </label>
          {good.length !== rows.length && <Banner tone="warn" className="mt-3">Fix every row before importing.</Banner>}
          {imp.isError && <Banner tone="error" className="mt-3">{errorMessage(imp.error)}</Banner>}
          {imp.data && (
            <Banner tone="success" className="mt-3" title={`${imp.data.imported} advances imported`}>
              {imp.data.skipped.length > 0 && `${imp.data.skipped.length} skipped (already imported).`}
            </Banner>
          )}
          <div className="mt-4">
            <ActionButton perm="ledger-entries:create" leadingIcon={<UploadSimple />} disabled={!ready} loading={imp.isPending} onClick={() => void submit()}>
              Import {good.length} advances
            </ActionButton>
          </div>
        </Section>
      )}
    </ConsolePage>
  )
}
